<script>
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faXmark, faSpinner } from '@fortawesome/pro-light-svg-icons';

  export let show = false;
  export let targetStudy = null; // { _id, studyName, protocolNumber, ... }

  const dispatch = createEventDispatcher();
  const API_BASE_URL = '/api/proxy';

  let studies = [];
  let sourceStudyGUID = '';
  let loadingStudies = false;
  let cloning = false;
  let error = null;

  function handleClose() {
    show = false;
    error = null;
    cloning = false;
    sourceStudyGUID = '';
    dispatch('close');
  }

  function handleOverlayClick(event) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  async function loadStudies() {
    if (!show) return;
    loadingStudies = true;
    error = null;
    try {
      const res = await fetch(
        `${API_BASE_URL}/studies?page=1&pageSize=999999&searchText=&sortOrder=asc`
      );
      if (!res.ok) {
        throw new Error('Failed to fetch studies');
      }
      const data = await res.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      const filtered = targetStudy?._id
        ? list.filter((s) => s?._id !== targetStudy._id)
        : list;

      studies = filtered;
      sourceStudyGUID = filtered?.[0]?._id || '';
    } catch (e) {
      console.error('Error loading studies for clone modal:', e);
      error = e.message || 'Failed to load studies';
      studies = [];
      sourceStudyGUID = '';
    } finally {
      loadingStudies = false;
    }
  }

  async function handleClone() {
    try {
      if (!targetStudy?._id) {
        throw new Error('Please select a target study first.');
      }
      if (!sourceStudyGUID) {
        throw new Error('Please select a source study.');
      }
      if (sourceStudyGUID === targetStudy._id) {
        throw new Error('Source and target studies must be different.');
      }

      cloning = true;
      error = null;

      const res = await fetch(`${API_BASE_URL}/schedulerJobConfigs/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceStudyGUID,
          targetStudyGUID: targetStudy._id,
          overwrite: true
        })
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result?.message || `Clone failed: ${res.status} ${res.statusText}`);
      }

      dispatch('cloned', result);
      handleClose();
    } catch (e) {
      console.error('Error cloning scheduler job configs:', e);
      error = e.message || 'Clone failed';
    } finally {
      cloning = false;
    }
  }

  // When opened, load studies list
  $: if (show) {
    loadStudies();
  }
</script>

{#if show}
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    transition:fade={{ duration: 200 }}
    on:click={handleOverlayClick}
    role="button"
    tabindex="0"
    on:keydown={(e) => ['Escape', 'Enter', ' '].includes(e.key) && handleClose()}
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      on:click|stopPropagation
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Clone scheduler job configs
        </h2>
        <button
          type="button"
          on:click={handleClose}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} class="h-5 w-5" />
        </button>
      </div>

      <!-- Body -->
      <div class="p-4 space-y-4">
        <div class="text-sm text-gray-700 dark:text-gray-200">
          <div class="font-medium">Target study</div>
          <div class="text-gray-600 dark:text-gray-300">
            {#if targetStudy}
              {targetStudy.studyName} ({targetStudy.protocolNumber})
            {:else}
              No target study selected
            {/if}
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            Source study
          </label>

          {#if loadingStudies}
            <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <FontAwesomeIcon icon={faSpinner} spin />
              Loading studies…
            </div>
          {:else}
            <select
              bind:value={sourceStudyGUID}
              class="w-full border rounded px-2 py-1 bg-white dark:bg-gray-700 dark:border-gray-600 text-gray-900 dark:text-gray-100"
              disabled={!studies.length || cloning}
            >
              {#if !studies.length}
                <option value="">No available studies</option>
              {/if}
              {#each studies as s}
                <option value={s._id}>
                  {s.studyName} ({s.protocolNumber})
                </option>
              {/each}
            </select>
          {/if}
        </div>

        <div class="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3">
          Conflicts are <span class="font-semibold">OVERWRITTEN</span> in the target study (same jobId).
        </div>

        {#if error}
          <div class="text-sm text-red-600 dark:text-red-400">{error}</div>
        {/if}
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          on:click={handleClose}
          class="bg-gray-500 text-white py-1 px-3 rounded hover:bg-gray-700"
          disabled={cloning}
        >
          Cancel
        </button>
        <button
          type="button"
          on:click={handleClone}
          class="bg-sky-700 text-white py-1 px-3 rounded hover:bg-sky-800 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={cloning || loadingStudies || !targetStudy?._id || !sourceStudyGUID}
        >
          {#if cloning}
            <span class="inline-flex items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} spin />
              Cloning…
            </span>
          {:else}
            Clone
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}


